import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Field, inputCls, itDate } from "@/components/kit";
import { Card, PublicShell, useLang } from "@/components/PublicShell";
import { api, errMsg } from "@/lib/api";

export default function Rsvp() {
  const { token } = useParams();
  const [ev, setEv] = useState(null);
  const [err, setErr] = useState("");
  const [f, setF] = useState({ nome: "", presenza: "si", adulti: 1, bambini: 0, accompagnatori: "", allergie: "", tel: "", note: "" });
  const [state, setState] = useState("");
  const [lang, setLang, t] = useLang();
  useEffect(() => {
    api.get(`/public/rsvp/${token}`).then((r) => setEv(r.data)).catch((e) => setErr(errMsg(e)));
  }, [token]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const send = async (e) => {
    e.preventDefault();
    setState("busy");
    try {
      await api.post(`/public/rsvp/${token}`, f);
      setState("done");
    } catch (x) {
      setErr(errMsg(x));
      setState("");
    }
  };
  const yes = f.presenza === "si";
  return (
    <PublicShell testId="rsvp-page" lang={lang} setLang={setLang}>
      <Card>
        {!ev && !err && <div className="text-sm text-[#7B929C]">Caricamento…</div>}
        {err && !ev && <div className="text-[#991B1B]" data-testid="rsvp-error">{err}</div>}
        {ev && state === "done" && (
          <div className="text-center space-y-3 py-6" data-testid="rsvp-done">
            <CheckCircle2 size={44} className="mx-auto text-[#34B3B0]" />
            <h1 className="font-serif-it text-3xl font-semibold">{yes ? t("Ci vediamo alla festa!", "See you at the party!") : t("Grazie per averci avvisato", "Thank you for letting us know")}</h1>
            <p className="text-[#47606B]">{t(`La tua risposta è arrivata a ${ev.cliente_nome}.`, `Your reply has been sent to ${ev.cliente_nome}.`)} {yes && t("Se cambia qualcosa, puoi compilare di nuovo con lo stesso nome.", "If anything changes, just fill in the form again with the same name.")}</p>
          </div>
        )}
        {ev && state !== "done" && (
          <form onSubmit={send} className="space-y-4">
            <div className="text-center">
              <div className="text-xs uppercase tracking-[0.25em] text-[#34B3B0]">{ev.evento_tipo}</div>
              <h1 className="font-serif-it text-3xl sm:text-4xl font-semibold mt-1" data-testid="rsvp-title">{ev.cliente_nome}</h1>
              <p className="text-sm text-[#47606B] mt-1">{itDate(ev.evento_data)}{ev.ora_inizio && ` • ${t("ore", "at")} ${ev.ora_inizio}`} • {ev.sede}</p>
            </div>
            {ev.closed ? <div className="rounded-lg bg-[#FEF3C7] p-4 text-sm" data-testid="rsvp-closed">{t(`Le conferme sono chiuse: contatta direttamente ${ev.cliente_nome}.`, `Replies are closed: please contact ${ev.cliente_nome} directly.`)}</div> : (
              <>
                <div className="grid grid-cols-2 gap-2">{[["si", t("Ci sarò 🥂", "I'll be there 🥂")], ["no", t("Non potrò esserci", "I can't make it")]].map(([v, l]) => <button type="button" key={v} onClick={() => setF({ ...f, presenza: v })} className={`rounded-xl border p-3 text-sm transition-colors ${f.presenza === v ? "border-[#0B6E8F] bg-[#EAF5F7] font-semibold" : "border-[#D4E6EC]"}`} data-testid={`rsvp-${v}`}>{l}</button>)}</div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label={t("Nome e cognome *", "Full name *")} wide><input required className={inputCls} value={f.nome} onChange={set("nome")} data-testid="rsvp-nome" /></Field>
                  {yes && <>
                    <Field label={t("Adulti (te compreso)", "Adults (including you)")}><input type="number" min="1" max="20" className={inputCls} value={f.adulti} onChange={set("adulti")} data-testid="rsvp-adulti" /></Field>
                    <Field label={t("Bambini", "Children")}><input type="number" min="0" max="20" className={inputCls} value={f.bambini} onChange={set("bambini")} data-testid="rsvp-bambini" /></Field>
                    <Field label={t("Nomi degli accompagnatori", "Names of your companions")} wide><input className={inputCls} value={f.accompagnatori} onChange={set("accompagnatori")} placeholder={t("separati da virgola", "comma separated")} data-testid="rsvp-acc" /></Field>
                    <Field label={t("Allergie o intolleranze", "Allergies or dietary needs")} wide><input className={inputCls} value={f.allergie} onChange={set("allergie")} placeholder={t("es. glutine, lattosio, vegetariano", "e.g. gluten, lactose, vegetarian")} data-testid="rsvp-allergie" /></Field>
                  </>}
                  <Field label={t("Telefono", "Phone")}><input className={inputCls} value={f.tel} onChange={set("tel")} data-testid="rsvp-tel" /></Field>
                  <Field label={t("Messaggio", "Message")}><input className={inputCls} value={f.note} onChange={set("note")} data-testid="rsvp-note" /></Field>
                </div>
                {err && <div className="text-sm text-[#991B1B]">{err}</div>}
                <Button type="submit" disabled={state === "busy"} className="w-full rounded-full h-12 text-base" data-testid="rsvp-submit">{state === "busy" && <Loader2 size={16} className="mr-2 animate-spin" />} {t("Invia risposta", "Send reply")}</Button>
              </>
            )}
          </form>
        )}
      </Card>
    </PublicShell>
  );
}
